import { LRUCache } from 'lru-cache';
import { formLookupKey } from './utils';
/*
 * A map that lazily creates values. The factory function is called only when a key
 * is accessed for the first time. Subsequent calls to `get()` return the cached value
 * unless it has been evicted due to `maxSize` or `maxAge` limits.
 *
 * It is possible to implement e.g. positive cache by using `isCacheableValue()`
 * config option. If that method returns `false`, the value is not stored to cache.
 * Note that using this option doesn't change the concurrent promise handling:
 * also in this case all concurrent `get()` calls are grouped so that only one
 * call to `valueFactory` is made. (If we wouldn't group these calls, all concurrent
 * `get()` calls were cache misses, i.e. affecting significantly cases where the
 * `isCacheableValue()` returns `true`.)
 */
export class Mapping {
    delegate;
    pendingPromises = new Map();
    opts;
    /**
     * Prefer constructing the class via createCacheMap() and createLazyMap()
     *
     * @internal
     **/
    constructor(opts) {
        if ('maxSize' in opts) {
            this.delegate = new LRUCache({
                maxSize: opts.maxSize,
                sizeCalculation: () => 1,
                ttl: opts.maxAge
            });
        }
        else {
            this.delegate = new Map();
        }
        this.opts = {
            isCacheableValue: () => true,
            ...opts
        };
    }
    async get(key) {
        const lookupKey = formLookupKey(key);
        const pendingPromise = this.pendingPromises.get(lookupKey);
        if (pendingPromise !== undefined) {
            return await pendingPromise;
        }
        else {
            let item = this.delegate.get(lookupKey);
            if (item === undefined) {
                const promise = this.opts.valueFactory(key);
                this.pendingPromises.set(lookupKey, promise);
                let value;
                try {
                    value = await promise;
                }
                finally {
                    this.pendingPromises.delete(lookupKey);
                }
                item = { key, value };
                if (this.opts.isCacheableValue(value)) {
                    this.delegate.set(lookupKey, item);
                }
            }
            return item.value;
        }
    }
    set(key, value) {
        this.delegate.set(formLookupKey(key), { key, value });
    }
    invalidate(predicate) {
        for (const [lookupKey, item] of this.delegate.entries()) {
            if (predicate(item.key)) {
                this.delegate.delete(lookupKey);
            }
        }
    }
    *values() {
        for (const item of this.delegate.values()) {
            yield item.value;
        }
    }
}
export const createCacheMap = (opts) => {
    return new Mapping(opts);
};
export const createLazyMap = (opts) => {
    return new Mapping(opts);
};
//# sourceMappingURL=Mapping.js.map